
import { useNavigate, useParams, } from 'react-router-dom';
import axios, { HttpStatusCode } from 'axios';
import { Formik, Form, FormikErrors } from 'formik';
import { useState, useEffect } from 'react';
import { useTranslation } from "../../../i18n/useTranslation";
import { toast } from 'react-toastify';
import styles from './building-sites-form.module.scss';
import * as yup from 'yup';
import { Country } from '../../../components/order/models';
import { BuildingSiteFormViewModel } from './building-sites-form-view.model';
import { IBuildingSiteDto } from './building-sites-form-dto.interface';
import { Validator } from '../../../shared/constants/validator';
import { addBuildingSite, getEditedBuildingSite, htmlId, updateBuildingSite } from './building-sites-form.service';
import { BUILDING_SITES_PATHS } from '../../../shared/constants/breadcrumb.constants';
import React from 'react';
import { Utils } from '../../../shared/constants/utilities.constants';
import { BuildingSiteFormSkeleton } from './BuildingSiteFormSkeleton';
import { Button, CircularProgress } from '@mui/material';

export interface IContactUsFormInput {
    isEdit: boolean;
    readonly: boolean;
}

const BuildingSitesForm: React.FC<IContactUsFormInput> = ({isEdit, readonly}) => {
    const { t } = useTranslation('translation');
    const [countries, setCountries] = useState<Country[]>([]);
    const [initialValues, setInitialValues] = useState<BuildingSiteFormViewModel>(new BuildingSiteFormViewModel());
    const { contractorUuid } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isCreatedLoading, setIsCreatedLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const showError = (message: string) => toast.error(message);

    useEffect(() => {
        axios.get('countries')
            .then(response => {
                const countries = response?.data as Country[];
                setCountries(countries || []);
            })
    }, []);

    useEffect(() => {
        if (isEdit && contractorUuid) {
            handleEditedBulidingSite(contractorUuid);
        } else {
            setIsLoading(false);
        }
    }, []);

    const handleEditedBulidingSite = (contractorUuid: string) => {
        getEditedBuildingSite(contractorUuid).then((response) => {
            if (response) {
              const model: BuildingSiteFormViewModel = response.data;
              setInitialValues(model);
              setIsLoading(false);
            }
        })
    }

    const getModelDto = (values: BuildingSiteFormViewModel, code?: string): IBuildingSiteDto => {
        const country: Country = countries.find(x => x.id === values.countryId) as Country;
        const dto: IBuildingSiteDto = {
            name: values.name,
            code: code,
            street: values.street,
            country: country.code,
            countryId: values.countryId,
            postalCode: values.postalCode,
            city: values.city,
            mobile: values.mobile,
            phone: values.phone,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            contractorUuid: contractorUuid,
            contactId: initialValues.contactId,
            addressId: initialValues.addressId
        }
        return dto;
    }

    const isValid = (errors: FormikErrors<BuildingSiteFormViewModel>): boolean => {
        return Object.keys(errors).length === 0;
    }
    
    const validationSchema = yup.object<BuildingSiteFormViewModel>().shape({
        name: yup.string().required().min(Validator.minLengthName).max(Validator.maxLengthName),
        street: yup.string().required().min(Validator.minLengthName).max(Validator.maxLengthName),
        countryId: yup
        .string()
        .test(
            "required-guid",
            t("REQUIRED_FIELD"),
            (value) => {
            return !!value && value !== Utils.defaultUuid;
            }
        )
        .required(t("REQUIRED_FIELD")),
        postalCode: yup.string().required().max(Validator.maxLengthName),
        city: yup.string().required().min(Validator.minLengthName).max(Validator.maxLengthName),
        mobile: yup.string().matches(Validator.phoneNumberRegExp),
        phone: yup.string().required().matches(Validator.phoneNumberRegExp),
        email: yup.string().email(),
        contact: yup.string().min(Validator.minLengthName).max(Validator.maxLengthName),
        reference: yup.string().min(Validator.minLengthName).max(Validator.maxLengthName),
    });

    const displayFillRequiredData = (errors: FormikErrors<BuildingSiteFormViewModel>) => {
        if (!isValid(errors)) {
            showError(t('register.fillAllRequiredData'));
        }
    }

    const update = (values: BuildingSiteFormViewModel, errors: FormikErrors<BuildingSiteFormViewModel>): void | Promise<any> => {
        if (isValid(errors)) {
            setIsCreatedLoading(true);
            updateBuildingSite(getModelDto(values, values.code)).then((response) => {
                if (response && response.status === HttpStatusCode.Ok) {
                    setIsCreatedLoading(false);
                    navigate(BUILDING_SITES_PATHS[0].link);
                } else {
                    showError(t('commonErrors.SENDING_MESSAGE'));
                    setIsCreatedLoading(false);
                }
            }).catch(() => {
                setIsCreatedLoading(false);
                showError(t('commonErrors.SENDING_MESSAGE'))
            });
        }
    }

    const add = (values: BuildingSiteFormViewModel, errors: FormikErrors<BuildingSiteFormViewModel>): void | Promise<any> => {
        if (isValid(errors)) {
            setIsCreatedLoading(true);
            addBuildingSite(getModelDto(values, '')).then((response) => {
                if (response && response.status === HttpStatusCode.Ok) {
                    setIsCreatedLoading(false);
                    navigate(BUILDING_SITES_PATHS[0].link);
                } else {
                    showError(t('commonErrors.SENDING_MESSAGE'));
                    setIsCreatedLoading(false);
                }
            }).catch(() => {
                setIsCreatedLoading(false);
                showError(t('commonErrors.SENDING_MESSAGE'))
            });
        }
    }

    return (
        <div className={styles.form_container}>
            {isLoading ? <BuildingSiteFormSkeleton/> : 
            <React.Fragment>
            <Formik
                enableReinitialize
                validateOnMount 
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={() => {}}
            >{({
                values, handleChange, errors, touched }) =>
                <Form
                    autoComplete='off'
                    spellCheck='false'
                    className={styles.form} 
                    onMouseEnter={() => { }} 
                    onSubmit={(e) => {
                        e.preventDefault();
                        !isEdit ? add(values, errors) : update(values, errors);
                    }}>
                    <div className={styles.form_content}>   
                        <div className={styles.content}>
                            <label className={styles.main_header_text}>
                                {t(isEdit && !readonly ? 'EDIT_BUILDING_SITE': '')}
                                {t(!isEdit && !readonly ? 'ADD_NEW_BUILDING_SITE': '')}
                                {t(readonly ? 'VIEW_BUILDING_SITE': '')}
                            </label>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('BUILDING_SITE')}*</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['name'] ? e.setCustomValidity(t(errors['name'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-name-input`}
                                    name="name" 
                                    value={values.name}
                                    onChange={handleChange}
                                    readOnly={readonly}
                                    className={readonly ? styles.readonly : ''}
                                >
                                </input>
                            </div>
                            {(readonly || isEdit) &&
                                <div className={styles.form_row}>
                                    <label className={styles.header_text}>{t('CODE')}*</label>
                                    <input
                                        ref={(e) => {
                                            if (e) {
                                                errors['code'] ? e.setCustomValidity(t(errors['code'])) :  e.setCustomValidity('');          
                                            }
                                        }}
                                        id = {`${htmlId}-code-input`}
                                        name="code" 
                                        value={values.code}
                                        onChange={handleChange}
                                        readOnly={isEdit || readonly}
                                        disabled={isEdit || readonly}
                                        className={isEdit || readonly ? styles.readonly : ''}
                                    >
                                    </input>
                                </div>
                            }
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('STREET')}*</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['street'] ? e.setCustomValidity(t(errors['street'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-street-input`}
                                    name="street" 
                                    value={values.street}
                                    onChange={handleChange}
                                    readOnly={readonly}
                                    className={readonly ? styles.readonly : ''}
                                >
                                </input>
                            </div>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('COUNTRY')}*</label>
                                <select
                                    disabled={readonly}
                                    className={readonly ? styles.readonly : ''}
                                    required
                                    ref={(e) => {
                                        if (e) {
                                            errors['countryId'] ? e.setCustomValidity(t(errors['countryId'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-country-code-select`}
                                    name="countryId" 
                                    value={values.countryId} onChange={handleChange}>
                                    <option value={Utils.defaultUuid} disabled hidden></option>
                                    {countries.map((country: Country, index: number) => (
                                        <option 
                                            key={index} 
                                            value={country?.id}>
                                                {country?.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('ZIP')}*</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['postalCode'] ? e.setCustomValidity(t(errors['postalCode'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-zip-input`}
                                    name="postalCode"
                                    value={values.postalCode}
                                    onChange={handleChange}
                                    readOnly={readonly}
                                    className={readonly ? styles.readonly : ''}
                                >
                                </input>
                            </div>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('CITY')}*</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['city'] ? e.setCustomValidity(t(errors['city'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-city-input`}
                                    name="city" 
                                    value={values.city}
                                    readOnly={readonly}
                                    onChange={handleChange}
                                    className={readonly ? styles.readonly : ''}
                                >
                                </input>
                            </div>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('MOBILE')}</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['mobile'] ? e.setCustomValidity(t(errors['mobile'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-mobile-input`}
                                    name="mobile" 
                                    value={values.mobile} 
                                    readOnly={readonly}
                                    onChange={handleChange}
                                    className={readonly ? styles.readonly : ''}
                                >
                                </input>
                            </div>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('PHONE')}*</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['phone'] ? e.setCustomValidity(t(Validator.translateMainCode + 'PHONE_NUMBER')) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-phone-input`}
                                    name="phone" 
                                    value={values.phone} 
                                    readOnly={readonly}
                                    onChange={handleChange}
                                    className={readonly ? styles.readonly : ''}
                                >
                                </input>
                            </div>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('EMAIL')}</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['email'] ? e.setCustomValidity(t(errors['email'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-email-input`}
                                    name="email"
                                    type='email'
                                    value={values.email}
                                    readOnly={readonly}
                                    onChange={handleChange}
                                    className={readonly ? styles.readonly : ''}
                                >
                                </input>
                            </div>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('FIRST_NAME')}</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['firstName'] ? e.setCustomValidity(t(errors['firstName'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-first-name-input`}
                                    name="firstName" 
                                    value={values.firstName}
                                    readOnly={readonly}
                                    onChange={handleChange}
                                    className={readonly ? styles.readonly : ''}
                                >
                                </input>
                            </div>
                            <div className={styles.form_row}>
                                <label className={styles.header_text}>{t('LAST_NAME')}</label>
                                <input
                                    ref={(e) => {
                                        if (e) {
                                            errors['lastName'] ? e.setCustomValidity(t(errors['lastName'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-last-name-input`}
                                    name="lastName" 
                                    value={values.lastName}
                                    readOnly={readonly}
                                    onChange={handleChange}
                                    className={readonly ? styles.readonly : ''}
                                >
                                </input>
                            </div>
                            <div className={styles.button_content}>
                                {!readonly && 
                                    <Button
                                        className={styles.send_btn}
                                        variant="contained"
                                        type='submit'
                                        fullWidth
                                        id = {`${htmlId}-submit-btn`}
                                        disabled={readonly || isCreatedLoading}
                                        onClick={() => {
                                            displayFillRequiredData(errors)
                                        }}
                                        sx={{
                                            height: 42,
                                            backgroundColor: "#821818",
                                            color: "#fff",
                                            textTransform: "none",
                                            boxShadow: "none",
                                            "&:hover": {
                                                backgroundColor: "#821818",
                                                boxShadow: "none",
                                            },
                                            "&:active": {
                                                boxShadow: "none",
                                            },
                                            "&:focus": {
                                                outline: "none",
                                            },
                                        }}
                                        disableRipple
                                    >
                                        {isCreatedLoading ? <CircularProgress size={22} color="inherit" /> : t("SEND")}
                                    </Button>
                                }
                            </div>
                        </div>
                    </div>
                </Form >
                }
            </Formik >
            </React.Fragment>
            }
        </div>
    );
};

export default BuildingSitesForm;